<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { HLDivider } from "@platform-ui/highrise";
import { Toaster } from "@/components/ui/sonner";
import {
  LayoutDashboard,
  Link,
  Anchor,
  Layers,
  LogOut,
  BookOpen,
  AlertCircle,
  BarChart3,
  Network,
} from "lucide-vue-next";
import { useAuth } from "@/composables/useAuth";

const { t } = useI18n();
const route = useRoute();
const { user, isAuthenticated, logout, initAuth } = useAuth();

onMounted(() => {
  initAuth();
});

const navGroups = computed(() => [
  {
    label: "",
    items: [
      { title: t('admin.nav.dashboard'), path: "/", icon: LayoutDashboard },
    ],
  },
  {
    label: t('admin.nav.integration'),
    items: [
      { title: t('admin.nav.providers'), path: "/providers", icon: Link },
      { title: t('admin.nav.anchors'), path: "/anchors", icon: Anchor },
    ],
  },
  {
    label: t('admin.nav.analytics'),
    items: [
      { title: t('admin.nav.aiUsage'), path: "/ai-usage", icon: BarChart3 },
    ],
  },
  {
    label: t('admin.nav.system'),
    items: [
      { title: t('admin.nav.architecture'), path: "/architecture", icon: Network },
    ],
  },
  {
    label: t('admin.nav.schema'),
    items: [
      { title: t('admin.nav.documentTypes'), path: "/document-types", icon: Layers },
    ],
  },
  {
    label: t('admin.nav.developer'),
    items: [
      { title: t('admin.nav.apiDocs'), path: "/api-docs", icon: BookOpen },
      { title: t('admin.nav.errorCodes'), path: "/error-codes", icon: AlertCircle },
    ],
  },
]);

function isActive(path: string) {
  if (path === "/") return route.path === "/";
  return route.path === path || route.path.startsWith(path + "/");
}
</script>

<template>
  <template v-if="isAuthenticated">
    <div class="flex h-screen">
      <aside class="w-64 border-r border-border bg-sidebar flex flex-col">
        <div class="flex items-center gap-3 p-4 border-b border-sidebar-border">
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
            {{ t('admin.brand.logo') }}
          </div>
          <div class="flex flex-col">
            <span class="text-sm font-semibold leading-tight">{{ t('admin.brand.name') }}</span>
            <span class="text-xs text-muted-foreground leading-tight">{{ t('admin.brand.subtitle') }}</span>
          </div>
        </div>
        <nav class="flex-1 overflow-y-auto p-3 space-y-4">
          <div v-for="group in navGroups" :key="group.label">
            <p v-if="group.label" class="px-3 mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {{ group.label }}
            </p>
            <ul class="space-y-0.5">
              <li v-for="item in group.items" :key="item.path">
                <router-link
                  :to="item.path"
                  class="flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors"
                  :class="isActive(item.path)
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/50'"
                >
                  <component :is="item.icon" class="size-4 shrink-0" />
                  <span>{{ item.title }}</span>
                </router-link>
              </li>
            </ul>
          </div>
        </nav>
        <div class="border-t border-sidebar-border p-3">
          <div class="flex items-center justify-between gap-2 rounded-md px-3 py-2">
            <span class="text-xs text-muted-foreground truncate" :title="user?.email">
              {{ user?.email }}
            </span>
            <button
              @click="logout"
              class="flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
              :title="t('admin.brand.signOut')"
            >
              <LogOut class="size-3.5" />
            </button>
          </div>
        </div>
      </aside>
      <div class="flex-1 flex flex-col overflow-hidden">
        <header class="flex h-14 items-center gap-2 border-b px-6">
          <HLDivider vertical class="h-6" />
          <span class="text-sm font-medium capitalize">{{ String(route.name || '').replace(/-/g, ' ') }}</span>
        </header>
        <main class="flex-1 overflow-y-auto p-6">
          <router-view />
        </main>
      </div>
    </div>
  </template>
  <template v-else>
    <router-view />
  </template>
  <Toaster position="top-right" :rich-colors="true" />
</template>
